import api from '@/lib/axios';
import { logger } from '@/lib/logger';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RegisterData {
  username: string;
  password: string;
  email?: string;
  nombre?: string;
}

// Issue #14: Permisos de usuario para clientes y marcas
export interface UserPermisos {
  clientes: number[];
  marcas: string[];
  tiene_todos_clientes: boolean;
  tiene_todas_marcas: boolean;
}

// RBAC dinámico (2026-04-26)
export interface ModuloAccesible {
  id: number;
  codigo: string;
  nombre: string;
  ruta?: string;
  icono?: string;
  grupo: 'principal' | 'admin';
  orden: number;
}

export interface User {
  id: number;
  username: string;
  nombre: string;
  email: string;
  rol: 'admin' | 'usuario' | 'user';  // legacy: admin/user
  pais: string;
  // Issue #14: Campos de permisos
  tiene_todos_clientes?: boolean;
  tiene_todas_marcas?: boolean;
  permisos?: UserPermisos;
  // RBAC dinámico
  rol_codigo?: string | null;
  rol_nombre?: string | null;
  es_admin?: boolean;
  modulos_accesibles?: ModuloAccesible[];
  permisos_modulos?: string[];  // ['cotizaciones:ver', 'pedidos:ver', ...] o ['*'] si admin
  // Impersonation
  impersonated?: boolean;
  impersonator_username?: string;
}

export interface AuthResponse {
  success: boolean;
  user?: User;
  token?: string;
  message?: string;
}

/**
 * Capa fina sobre los endpoints de autenticación.
 *
 * 2026-04-23: removido TODO acceso a localStorage. El token de sesión
 * viaja en cookie HttpOnly (withCredentials en lib/axios). El usuario se
 * obtiene SIEMPRE llamando al backend — no hay cache cliente.
 */
class AuthService {
  /** Inicia sesión con username y password. */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/api/auth/login', credentials);
      return response.data;
    } catch (error) {
      // Issue #14 Fix: Backend retorna 'error', no 'message'
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string; message?: string } } };
        throw new Error(axiosError.response?.data?.error || axiosError.response?.data?.message || 'Error al iniciar sesión');
      }
      throw new Error('Error al iniciar sesión');
    }
  }

  /** Cierra la sesión del usuario. */
  async logout(): Promise<void> {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      logger.error('Error al cerrar sesión:', error);
    }
  }

  /** Registra un nuevo usuario. */
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/api/auth/register', data);
      return response.data;
    } catch (error) {
      // Issue #14 Fix: Backend retorna 'error', no 'message'
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string; message?: string } } };
        throw new Error(axiosError.response?.data?.error || axiosError.response?.data?.message || 'Error al registrar usuario');
      }
      throw new Error('Error al registrar usuario');
    }
  }

  /**
   * Verifica la sesión contra el backend y devuelve el User cuando está
   * autenticado. Retorna null si la cookie expiró / no es válida.
   */
  /** Impersona a otro usuario (solo admin). Retorna el User target. */
  async impersonate(targetUserId: number): Promise<User | null> {
    try {
      const response = await api.post<{ success: boolean; user?: User; error?: string }>(
        `/api/auth/impersonate/${targetUserId}`
      );
      return response.data.user || null;
    } catch (error) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string } } };
        throw new Error(axiosError.response?.data?.error || 'Error al impersonar');
      }
      throw new Error('Error al impersonar');
    }
  }

  /** Detiene la impersonation y restaura la sesión del admin. */
  async stopImpersonate(): Promise<User | null> {
    try {
      const response = await api.post<{ success: boolean; user?: User; error?: string }>(
        '/api/auth/stop-impersonate'
      );
      return response.data.user || null;
    } catch (error) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string } } };
        throw new Error(axiosError.response?.data?.error || 'Error al detener impersonation');
      }
      throw new Error('Error al detener impersonation');
    }
  }

  async checkAuth(): Promise<User | null> {
    try {
      const response = await api.get<{ authenticated: boolean; user?: User }>('/api/auth/check');
      if (response.data.authenticated && response.data.user) {
        return response.data.user;
      }
      return null;
    } catch (error) {
      logger.error('Error al verificar autenticación:', error);
      return null;
    }
  }
}

export default new AuthService();
